import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FaChartBar, FaTable, FaCode, FaHeartbeat, FaLayerGroup,
  FaShieldAlt, FaToggleOn, FaChartPie, FaBuilding, FaListAlt, FaUserPlus, FaTrashAlt, FaStethoscope, FaTimes, FaBars,
  FaClipboardList, FaGavel, FaHeadset, FaUsers, FaUndoAlt, FaCube, FaMagic, FaBookmark, FaEnvelope, FaCity, FaKey,
  FaHammer
} from 'react-icons/fa';

const SECTIONS = [
  { key: 'overview', label: 'Overview', icon: FaChartBar, path: '/cloud-staff/dashboard', group: 'main' },
  { key: 'crm-funnel', label: 'CRM Pipeline', icon: FaUsers, path: '/cloud-staff/crm-funnel', group: 'onboarding' },
  { key: 'company-generation', label: 'Company Generation', icon: FaBuilding, path: '/cloud-staff/company-generation', group: 'onboarding' },
  { key: 'company-health', label: 'Company Health', icon: FaStethoscope, path: '/cloud-staff/company-health', group: 'monitoring' },
  { key: 'account-creation', label: 'Account Creation', icon: FaUserPlus, path: '/cloud-staff/account-creation', group: 'onboarding' },
  { key: 'account-recovery', label: 'Account Recovery', icon: FaUndoAlt, path: '/cloud-staff/account-recovery', group: 'onboarding' },
  { key: 'property-manager', label: 'Property Manager', icon: FaCity, path: '/cloud-staff/property-manager', group: 'onboarding' },
  { key: 'property-access', label: 'Property Access', icon: FaKey, path: '/cloud-staff/property-access', group: 'onboarding' },
  { key: 'bulk-editor', label: 'Bulk Asset Editor', icon: FaTable, path: '/cloud-staff/bulk-editor', group: 'tools' },
  { key: 'asset-code-tool', label: 'Asset Code Tool', icon: FaCode, path: '/cloud-staff/asset-code-tool', group: 'tools' },
  { key: 'asset-code-dictionary', label: 'Asset Code Dictionary', icon: FaBookmark, path: '/cloud-staff/asset-code-dictionary', group: 'tools' },
  { key: 'asset-generation', label: 'Asset Generation', icon: FaLayerGroup, path: '/cloud-staff/asset-generation', group: 'tools' },
  { key: 'asset-generation-v2', label: 'Asset Generation v2', icon: FaMagic, path: '/cloud-staff/asset-generation-v2', group: 'tools' },
  { key: 'building-log', label: 'Building Log', icon: FaListAlt, path: '/cloud-staff/building-log', group: 'tools' },
  { key: 'deleted-logs', label: 'Deleted Logs', icon: FaTrashAlt, path: '/cloud-staff/deleted-logs', group: 'tools' },
  { key: 'user-activity', label: 'User Activity', icon: FaUsers, path: '/cloud-staff/user-activity', group: 'monitoring' },
  { key: 'system-health', label: 'System Health', icon: FaHeartbeat, path: '/cloud-staff/system-health', group: 'monitoring' },
  { key: 'data-integrity', label: 'Data Integrity', icon: FaShieldAlt, path: '/cloud-staff/data-integrity', group: 'monitoring' },
  { key: 'system-logs', label: 'System Logs', icon: FaClipboardList, path: '/cloud-staff/system-logs', group: 'monitoring' },
  { key: 'support', label: 'Support Tickets', icon: FaHeadset, path: '/cloud-staff/support', group: 'admin' },
  { key: 'compliance', label: 'Compliance', icon: FaGavel, path: '/cloud-staff/compliance', group: 'admin' },
  { key: 'feature-flags', label: 'Feature Flags', icon: FaToggleOn, path: '/cloud-staff/feature-flags', group: 'admin' },
  { key: 'metrics', label: 'SaaS Metrics', icon: FaChartPie, path: '/cloud-staff/metrics', group: 'admin' },
  { key: 'email-templates', label: 'Email Templates', icon: FaEnvelope, path: '/cloud-staff/email-templates', group: 'admin' },
  { key: 'tours', label: '3D Tour Add-on', icon: FaCube, path: '/cloud-staff/tours', group: 'tools' },
  { key: 'building-tools', label: 'Build Tracker', icon: FaHammer, path: '/cloud-staff/building-tools', group: 'building' },
];

const GROUPS = [
  { key: 'main', label: null },
  { key: 'onboarding', label: 'Onboarding' },
  { key: 'tools', label: 'Operational Tools' },
  { key: 'monitoring', label: 'Monitoring' },
  { key: 'admin', label: 'Administration' },
  // Internal build tracker — sits at the very bottom, below Administration.
  { key: 'building', label: 'Building Tools' },
];

const CloudStaffSidebar = ({ active, mobileOpen: mobileOpenProp, onClose: onCloseProp }) => {
  // Support both controlled (from parent) and self-managed mobile state
  const [internalOpen, setInternalOpen] = useState(false);
  const controlled = mobileOpenProp !== undefined;
  const isOpen = controlled ? mobileOpenProp : internalOpen;
  const handleClose = controlled ? onCloseProp : () => setInternalOpen(false);
  const handleOpen = () => setInternalOpen(true);

  const navGroups = GROUPS.map(group => {
    const items = SECTIONS.filter(s => s.group === group.key);
    if (items.length === 0) return null;
    return (
      <div key={group.key}>
        {group.label && <div className="cs-nav-group-label">{group.label}</div>}
        {items.map(item => (
          <Link
            key={item.key}
            to={item.path}
            className={`cs-nav-item ${active === item.key ? 'active' : ''}`}
            onClick={handleClose}
          >
            <item.icon />
            <span>{item.label}</span>
          </Link>
        ))}
      </div>
    );
  });

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="cs-sidebar">
        <nav className="cs-nav">{navGroups}</nav>
        <div className="cs-sidebar-footer">
          <p className="cs-version">CloudSafe Staff Tools v2.0</p>
        </div>
      </aside>

      {/* Mobile floating hamburger — shows when not controlled by parent */}
      {!controlled && (
        <button className="cs-floating-menu-btn" onClick={handleOpen} aria-label="Open menu">
          <FaBars />
        </button>
      )}

      {/* Mobile overlay */}
      {isOpen && (
        <div className="cs-mobile-overlay" onClick={handleClose} />
      )}

      {/* Mobile drawer */}
      <aside className={`cs-mobile-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="cs-mobile-sidebar-header">
          <span>Staff Tools</span>
          <button className="cs-mobile-sidebar-close" onClick={handleClose} aria-label="Close menu">
            <FaTimes />
          </button>
        </div>
        <nav className="cs-nav">{navGroups}</nav>
        <div className="cs-sidebar-footer">
          <p className="cs-version">CloudSafe Staff Tools v2.0</p>
        </div>
      </aside>
    </>
  );
};

export default CloudStaffSidebar;
